'use memo';

import {
  CampaignDetailModelState,
  DocumentTypes,
  type Form as GeneratedForm,
} from '@urbanx/agx.campaigns.client';
import { DocumentTypesMap } from '@urbanx/agx-ui-components';
import type { useFormLoader } from 'hooks/useFormLoader';
import type { useSelectedContract } from 'hooks/useSelectedContract';
import { sortMultiOptionButtonOptionsByType } from 'utils/documentUtil';
import FileList from '../Common/FileList';

export const contractSubmissionErrorMessage =
  'Failed to start Contract submission';

export const listDetailSeparator = '\u2022';

const qldContractStageLabels: Record<string, string> = {
  ContractDraft: 'Draft',
  BuyerSigning: 'Buyer Signing',
  SignedByBuyer: 'Signed by Buyer',
  VendorSigning: 'Vendor Signing',
  SignedByAllParties: 'Signed by all Parties',
  SubmissionIncomplete: 'Submission Incomplete',
  PendingCompliance: 'Pending Compliance',
  BackUp: 'Back Up',
  Accepted: 'Accepted',
  Unconditional: 'Unconditional',
  Settled: 'Settled',
  Terminated: 'Terminated',
};

const nswContractStageLabels: Record<string, string> = {
  ContractDraft: 'Draft',
  SalesAdviceDraft: 'Sales Advice Draft',
  BuyerSigning: 'Buyer Signing',
  VendorSigning: 'Vendor Signing',
  Signing: 'Signing',
  SubmissionIncomplete: 'Submission Incomplete',
  AwaitingExchange: 'Awaiting Exchange',
  PendingSubmission: 'Pending Submission',
  ContractRequested: 'Contract Requested',
  PendingCompliance: 'Pending Compliance',
  Accepted: 'Accepted',
  Unconditional: 'Unconditional',
  Settled: 'Settled',
  Terminated: 'Terminated',
  Cancelled: 'Cancelled',
};

type MultiOptionButtonOption = {
  text: string;
  documentType?: DocumentTypes;
  onClick?: () => void;
};

type ContractDocuments =
  | NonNullable<ReturnType<typeof useSelectedContract>>['documents']
  | undefined;

export const withCampaignFormConfig = (form: GeneratedForm) =>
  ({
    ...form,
    formConfigType: 'Campaign',
  }) as unknown as Parameters<
    ReturnType<typeof useFormLoader>['loadFormWithConfig']
  >[0];

export const getContractStageLabel = (
  state: CampaignDetailModelState | undefined,
  stage: string
) => {
  const stageLabels =
    state === CampaignDetailModelState.QLD
      ? qldContractStageLabels
      : nswContractStageLabels;

  return stageLabels[stage] ?? stage;
};

export const createFileDownloadOptions = (
  documents: ContractDocuments,
  setDownloadFilePath: (filePath: string) => void
) => {
  const options: MultiOptionButtonOption[] = [];

  if (!documents || documents.length === 0) return options;

  documents.forEach(document => {
    const documentType = document.documentType as DocumentTypes | undefined;

    if (documentType === DocumentTypes.SalesContract) return;

    options.push({
      text:
        (documentType
          ? DocumentTypesMap[documentType as keyof typeof DocumentTypesMap]
          : undefined) ??
        documentType ??
        'Document',
      documentType,
      onClick: () => {
        if (!document.containerFilePath) return;

        setDownloadFilePath(document.containerFilePath);
      },
    });
  });

  const textCount = new Map<string, number>();
  const uniqueDocumentList = options.map(item => {
    const count = textCount.get(item.text) || 0;
    textCount.set(item.text, count + 1);

    return {
      ...item,
      text: count > 0 ? `${item.text}-${count}` : item.text,
    };
  });

  const finalUniqueDocumentList = [
    ...new Map(uniqueDocumentList.map(item => [item.text, item])).values(),
  ];

  return sortMultiOptionButtonOptionsByType(finalUniqueDocumentList);
};

export const ContractFileList = ({
  documents,
  showDownload,
  state,
  fileDownloadOptions,
}: {
  documents: ContractDocuments;
  showDownload: boolean;
  state: CampaignDetailModelState | undefined;
  fileDownloadOptions: MultiOptionButtonOption[];
}) => {
  if (!documents || documents.length === 0 || !showDownload) return null;

  return (
    <FileList
      id="viewContractSubmissionPdfs"
      files={
        state === CampaignDetailModelState.NSW
          ? fileDownloadOptions.filter(
              file => file.documentType !== DocumentTypes.TitleSearch
            )
          : fileDownloadOptions
      }
    />
  );
};
